"use client";

import type React from "react";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
  Input,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ArrowLeft, Save, Copy, Mail, RotateCcw, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  emailAgentService,
  type EmailAgent,
} from "../../email-services/agentService";
import { EmailModelSelect } from "./EmailModelSelect";
import { EmailLanguageSelect } from "./EmailLanguageSelect";
import { EmailAgentFunctions } from "./EmailAgentFunctions";
import { EmailChatSettings } from "./EmailChatSettings";
import { EmailAgentKnowledgeBase } from "./EmailAgentKnowledgeBase";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { EditableEmailAgentName } from "./EditableEmailAgentName";

const wiggle = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.08) rotate(-6deg); }
  50% { transform: scale(1.1) rotate(6deg); }
  75% { transform: scale(1.08) rotate(-4deg); }
  100% { transform: scale(1.06) rotate(0deg); }
`;

interface EmailAgentConfigProps {
  agent: EmailAgent;
}

interface Message {
  type: "human" | "ai";
  content: string;
  subject?: string;
}

function getShortId(id: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

function generateConversationId(): string {
  const randomStr =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  return `web-${randomStr}`;
}

const EmailAgentConfig: React.FC<EmailAgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();

  const [agent, setAgent] = useState<EmailAgent>(initialAgent);
  const [prompt, setPrompt] = useState(agent.prompt || "");
  const [initialEmailSubject, setInitialEmailSubject] = useState(
    agent.initial_email_subject || ""
  );
  const [initialEmailContent, setInitialEmailContent] = useState(
    agent.initial_email_content || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  const [isChatMode, setIsChatMode] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [userSubject, setUserSubject] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [senderId, setSenderId] = useState<string>(() =>
    generateConversationId()
  );
  const [receiverId, setReceiverId] = useState<string>(() =>
    generateConversationId()
  );

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "gray.300",
      borderRadius: "6px",
      border: "2px solid transparent",
    },
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await emailAgentService.update(agent.agent_id, {
        prompt,
        initial_email_subject: initialEmailSubject,
        initial_email_content: initialEmailContent,
      });
      toast({
        title: "Agent saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving agent",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitialEmailBlur = async () => {
    try {
      await emailAgentService.update(agent.agent_id, {
        initial_email_subject: initialEmailSubject,
        initial_email_content: initialEmailContent,
      });
      toast({
        title: "Initial email saved",
        status: "success",
        duration: 2000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving initial email",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) console.log("Variables updated:", vars);
      setDynamicVariables(vars);
    },
    []
  );

  const handleStartChat = () => {
    setIsChatMode(true);
    if (initialEmailSubject || initialEmailContent) {
      setMessages([
        {
          type: "ai",
          subject: initialEmailSubject,
          content: initialEmailContent,
        },
      ]);
    }
  };

  const handleResetConversation = () => {
    setMessages([]);
    setUserInput("");
    setUserSubject("");
    setIsChatMode(false);
    setSenderId(generateConversationId());
    setReceiverId(generateConversationId());
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const userMessage = userInput.trim();
    const emailSubject = userSubject.trim();
    setUserInput("");
    setUserSubject("");

    const newMessages: Message[] = [
      ...messages,
      {
        type: "human",
        subject: emailSubject,
        content: userMessage,
      },
    ];
    setMessages(newMessages);
    setIsSendingMessage(true);

    try {
      const formattedDynamicVars: Record<string, string> = {};
      Object.keys(dynamicVariables).forEach((key) => {
        const value = dynamicVariables[key];
        formattedDynamicVars[key] = value.trim() === "" ? `<<${key}>>` : value;
      });

      const response = await fetch(
        `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:8000"
        }/api/email/ask-agent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agent_id: agent.agent_id,
            user_message: userMessage,
            user_subject: emailSubject,
            dynamic_vars_values: formattedDynamicVars,
            sender_id: senderId,
            receiver_id: receiverId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from agent");
      }

      const data = await response.json();
      const agentResponse =
        data.agent_response || data.response || "No response";
      const agentSubject = data.agent_subject || "Re: " + emailSubject;

      setMessages([
        ...newMessages,
        {
          type: "ai",
          subject: agentSubject,
          content: agentResponse,
        },
      ]);
    } catch (error: any) {
      console.error("[v0] Error sending message:", error);
      toast({
        title: "Error sending message",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const testPanel = useMemo(
    () => (
      <VStack
        w="300px"
        p={4}
        borderLeft="1px solid"
        borderColor="gray.200"
        spacing={4}
      >
        <Flex align="center" justify="space-between" w="full" bg="gray.100">
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={initialEmailContent}
          />
        </Flex>

        <Divider />

        {!isChatMode ? (
          <Box textAlign="center" py={8}>
            <Box as="span" fontSize="48px">
              <IconButton
                w="full"
                background="transparent"
                _hover={{ background: "transparent" }}
                aria-label="Start Chat"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#e0e0e0"
                      d="M5.5,40.5h37c1.933,0,3.5-1.567,3.5-3.5V11.543c0-1.933-1.567-3.5-3.5-3.5h-37 c-1.933,0-3.5,1.567-3.5,3.5V37C2,38.933,3.567,40.5,5.5,40.5z"
                    ></path>
                    <path
                      fill="#d9d9d9"
                      d="M44.482,12.759L24,27.763L3.518,12.758c0,0-0.095-0.066-0.236-0.182L26,40.5h16.5 c1.933,0,3.5-1.567,3.5-3.5V11.441c0-0.102-0.021-0.197-0.03-0.296C45.816,12.262,44.482,12.759,44.482,12.759z"
                    ></path>
                    <path
                      fill="#eee"
                      d="M6.745,40.5H42.5c1.933,0,3.5-1.567,3.5-3.5V11.5L6.745,40.5z"
                    ></path>
                    <path
                      fill="#e0e0e0"
                      d="M25.745,40.5H42.5c1.933,0,3.5-1.567,3.5-3.5V11.5L18.771,31.616L25.745,40.5z"
                    ></path>
                    <path
                      fill="#fb923c"
                      d="M3.603,12.759c0,0-1.334-0.938-1.488-2.055c-0.008,0.099-0.03,0.195-0.03,0.296 L2,11.473v17.799V37c0,1.933,1.567,3.5,3.5,3.5H7V15.247L3.603,12.759z"
                    ></path>
                    <path
                      fill="#fb923c"
                      d="M45.97,11.145c-0.154,1.117-1.488,1.614-1.488,1.614L41,15.31V40.5h1.5 c1.933,0,3.5-1.567,3.5-3.5v-7.729v-17.83C46,11.34,45.979,11.244,45.97,11.145z"
                    ></path>
                    <path
                      fill="#bcbcbc"
                      d="M3.42,13.31l20.623,14.973L44.665,13.31c0,0,0.937-0.661,1.335-1.531v-0.228 c-0.012-1.996-1.569-3.51-3.5-3.5h-37c-1.933,0-3.5,1.567-3.5,3.5v0.009C2.323,12.536,3.42,13.31,3.42,13.31z"
                    ></path>
                    <g>
                      <path
                        fill="#f5f5f5"
                        d="M42.5,8H24H5.5C3.567,8,2,9.536,2,11.5c0,1.206,1.518,2.258,1.518,2.258L24,28.256 l20.482-14.497c0,0,1.518-1.053,1.518-2.258C46,9.536,44.433,8,42.5,8z"
                      ></path>
                      <path
                        fill="#fb923c"
                        d="M43.246,8.082L24,21.5L4.754,8.082C3.18,8.419,2,9.797,2,11.5 c0,1.206,1.518,2.258,1.518,2.258L24,28.256l20.482-14.497c0,0,1.518-1.053,1.518-2.258C46,9.797,44.82,8.419,43.246,8.082z"
                      ></path>
                    </g>
                  </svg>
                }
                sx={{
                  "& svg": {
                    transition: "transform 0.2s ease, filter 0.2s ease",
                    transformOrigin: "center",
                  },
                  "&:hover svg": {
                    animation: `${wiggle} 0.45s ease-in-out`,
                    filter: "drop-shadow(0 0 6px rgba(251, 146, 60, 0.9))", // orange.400 glow
                  },
                }}
              />
            </Box>

            <Button
              mt={4}
              w="full"
              colorScheme="orange"
              bg="orange.500"
              _hover={{ bg: "orange.600" }}
              variant="solid"
              onClick={handleStartChat}
            >
              Start Email Thread
            </Button>
          </Box>
        ) : (
          <VStack w="full" h="calc(100vh - 200px)" spacing={0}>
            <Box
              position="relative"
              w="full"
              flex="1"
              display="flex"
              flexDirection="column"
              minH="0"
            >
              <IconButton
                aria-label="Reset conversation"
                icon={<RotateCcw size={16} />}
                size="sm"
                variant="ghost"
                onClick={handleResetConversation}
                title="Reset conversation"
                position="absolute"
                top="2"
                right="2"
                zIndex={2}
                bg="whiteAlpha.800"
                _hover={{ bg: "whiteAlpha.900" }}
                boxShadow="sm"
                borderRadius="full"
              />

              <VStack
                flex={1}
                w="full"
                overflowY="auto"
                spacing={3}
                align="stretch"
                css={scrollbarStyles}
                p={2}
                bg="gray.50"
                borderRadius="md"
                minH="0"
              >
                {messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    alignSelf={msg.type === "human" ? "flex-end" : "flex-start"}
                    maxW="85%"
                  >
                    <Box
                      bg={msg.type === "human" ? "orange.500" : "white"}
                      color={msg.type === "human" ? "white" : "gray.800"}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border={msg.type === "ai" ? "1px solid" : "none"}
                      borderColor="gray.200"
                    >
                      {msg.subject && (
                        <Text fontWeight="bold" mb={1} fontSize="xs">
                          {msg.subject}
                        </Text>
                      )}
                      {msg.content}
                    </Box>
                  </Box>
                ))}

                {isSendingMessage && (
                  <Box alignSelf="flex-start" maxW="85%">
                    <Box
                      bg="white"
                      color="gray.800"
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <style>
                        {`
                        @keyframes typing-dot {
                          0%, 60%, 100% { opacity: 0.3; }
                          30% { opacity: 1; }
                        }
                      `}
                      </style>
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.2s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.4s" }}
                      />
                    </Box>
                  </Box>
                )}

                <div ref={chatEndRef} />
              </VStack>
            </Box>

            <VStack w="full" mt={3} spacing={2}>
              <Input
                value={userSubject}
                onChange={(e) => setUserSubject(e.target.value)}
                placeholder="Email subject..."
                size="sm"
                disabled={isSendingMessage}
                bg="white"
              />
              <Flex w="full" gap={2}>
                <Input
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your email message..."
                  size="sm"
                  disabled={isSendingMessage}
                  bg="white"
                />
                <IconButton
                  aria-label="Send message"
                  icon={<Send size={16} />}
                  size="sm"
                  colorScheme="orange"
                  onClick={handleSendMessage}
                  isLoading={isSendingMessage}
                  disabled={!userInput.trim() || isSendingMessage}
                />
              </Flex>
            </VStack>
          </VStack>
        )}
      </VStack>
    ),
    [
      handleDynamicVariablesChange,
      prompt,
      initialEmailContent,
      isChatMode,
      messages,
      userInput,
      userSubject,
      isSendingMessage,
      scrollbarStyles,
    ]
  );

  const mainContent = useMemo(
    () => (
      <Flex h="100vh" bg="white" color="gray.800" direction="column">
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center"
        >
          <IconButton
            aria-label="Back"
            icon={<ArrowLeft />}
            variant="ghost"
            color="gray.800"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/email-agents")}
          />
          <Box ml={4}>
            <EditableEmailAgentName
              agentId={agent.agent_id}
              initialName={agent.name || ""}
            />
            <Text color="gray.500" fontSize="sm">
              Email &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
              <IconButton
                aria-label="Copy Agent ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.agent_id)}
                color="gray.500"
              />
            </Text>
          </Box>
        </Flex>

        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={2}
        >
          <EmailModelSelect
            agentId={agent.agent_id}
            initialModel={agent.llm || "gpt-4o"}
            initialTemperature={agent.llm_temperature}
            onModelUpdate={(newModel) => setAgent({ ...agent, llm: newModel })}
          />
          <EmailLanguageSelect
            agentId={agent.agent_id}
            initialLanguage={agent.language || "en-US"}
            onLanguageUpdate={(newLang) =>
              setAgent({ ...agent, language: newLang })
            }
          />
        </Flex>

        <Flex flex={1} overflow="hidden" alignItems="stretch">
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            maxW="calc(100% - 600px)"
            minW="500px"
          >
            <VStack
              align="stretch"
              h="full"
              spacing={4}
              overflowY="auto"
              css={scrollbarStyles}
            >
              <Box position="relative" flex={0} minH="400px">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                  }}
                  resize="none"
                  fontSize="sm"
                  placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                  pr="100px"
                  height="100%"
                  onMouseEnter={() => setNoteVisible(true)}
                  onMouseLeave={() => setNoteVisible(false)}
                  isReadOnly={!isAdmin}
                />
                {!isAdmin && (
                  <Text fontSize="xs" color="orange.400" mt={1}>Prompt editing is restricted to administrators.</Text>
                )}
                <Button
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  size="sm"
                  colorScheme="orange"
                  bg="orange.500"
                  _hover={{ bg: "orange.600" }}
                  zIndex={2}
                >
                  Save
                </Button>
                {noteVisible && (
                  <Text
                    position="absolute"
                    bottom="8px"
                    left="8px"
                    fontSize="xs"
                    color="gray.500"
                  >
                    Use `{"{{_}}"}` to add variables.
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Initial Email Subject
                </Text>
                <Input
                  value={initialEmailSubject}
                  onChange={(e) => setInitialEmailSubject(e.target.value)}
                  onBlur={handleInitialEmailBlur}
                  placeholder="Enter the initial email subject"
                  size="md"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                  }}
                />
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Initial Email Body
                </Text>
                <Textarea
                  value={initialEmailContent}
                  onChange={(e) => setInitialEmailContent(e.target.value)}
                  onBlur={handleInitialEmailBlur}
                  placeholder="Enter the initial email body content"
                  size="md"
                  minH="150px"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "orange.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
                  }}
                />
              </Box>
            </VStack>
          </Box>

          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            css={scrollbarStyles}
          >
            <Accordion allowMultiple w="full">
              {isAdmin && (
                <EmailAgentFunctions
                  agentId={agent.agent_id}
                  initialFunctions={
                    agent.tools?.map((tool) => {
                      if (typeof tool === "string") {
                        try {
                          let parsed = JSON.parse(tool);
                          if (typeof parsed === "string") {
                            parsed = JSON.parse(parsed);
                          }
                          if (parsed && typeof parsed === "object") {
                            if (
                              typeof parsed.type === "string" &&
                              parsed.type.startsWith("{")
                            ) {
                              try {
                                parsed = JSON.parse(parsed.type);
                              } catch {
                                // Keep original parsed object
                              }
                            }
                          }
                          return parsed;
                        } catch (e) {
                          console.error("[v0] Error parsing tool:", tool, e);
                          return { type: tool, name: tool };
                        }
                      }
                      return tool;
                    }) || []
                  }
                />
              )}

              <EmailAgentKnowledgeBase agentId={agent.agent_id} />

              <EmailChatSettings
                agentId={agent.agent_id}
                initialMaxChatLength={agent.max_chat_length || 50}
                initialTone={agent.tone || "professional"}
              />
            </Accordion>
          </VStack>

          {testPanel}
        </Flex>
      </Flex>
    ),
    [
      agent,
      prompt,
      initialEmailSubject,
      initialEmailContent,
      noteVisible,
      navigate,
      testPanel,
      isSaving,
    ]
  );

  return isLoading ? (
    <Flex h="100vh" justify="center" align="center" bg="white">
      <Spinner size="xl" color="orange.500" />
    </Flex>
  ) : (
    mainContent
  );
};

export default EmailAgentConfig;
